import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { apiClient } from '@/services/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { AlertCircle, CheckCircle2 } from 'lucide-react'
import type { InvitationDetails } from '@flight-journal/shared'

const registerSchema = z.object({
  email: z.string().email('E-mail inválido'),
  password: z.string().min(8, 'A senha deve ter pelo menos 8 caracteres'),
  confirmPassword: z.string(),
  firstName: z.string().min(1, 'Nome é obrigatório'),
  lastName: z.string().min(1, 'Sobrenome é obrigatório'),
  role: z.enum(['operator', 'pilot', 'maintenance']),
  organizationName: z.string().optional(),
  invitationToken: z.string().optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "As senhas não coincidem",
  path: ["confirmPassword"],
}).refine((data) => {
  if (data.role === 'operator' && !data.organizationName) {
    return false
  }
  return true
}, {
  message: 'Nome da organização é obrigatório para operadores',
  path: ['organizationName'],
})

type RegisterForm = z.infer<typeof registerSchema>

export function RegisterPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null)
  const [loadingInvitation, setLoadingInvitation] = useState(false)

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      role: 'operator',
      organizationName: '',
      invitationToken: '',
    },
  })

  const watchRole = watch('role')

  // Check for invitation token in URL
  useEffect(() => {
    const token = searchParams.get('invitation')
    if (token) {
      fetchInvitation(token)
    }
  }, [searchParams])

  const fetchInvitation = async (token: string) => {
    setLoadingInvitation(true)
    setError('')
    try {
      const response = await apiClient.get(`/api/v1/invitations/${token}`)
      const invitationData: InvitationDetails = response.data

      setInvitation(invitationData)

      // Pre-fill form with invitation data
      setValue('email', invitationData.email)
      setValue('role', invitationData.role)
      setValue('invitationToken', invitationData.token)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Este convite não é válido ou expirou')
      setTimeout(() => navigate('/auth/register'), 3000)
    } finally {
      setLoadingInvitation(false)
    }
  }

  const onSubmit = async (data: RegisterForm) => {
    setLoading(true)
    setError('')

    try {
      await apiClient.post('/api/v1/auth/register', {
        user: {
          email: data.email,
          password: data.password,
          password_confirmation: data.confirmPassword,
          first_name: data.firstName,
          last_name: data.lastName,
          role: data.role,
          organization_name: data.organizationName,
          invitation_token: data.invitationToken,
          consented_to_data_collection: true,
        }
      })

      setSuccess(true)
      // Navigate to login after 3 seconds
      setTimeout(() => {
        navigate('/auth/login')
      }, 3000)
    } catch (err: any) {
      const errors = err.response?.data?.errors
      setError(Array.isArray(errors) ? errors[0] : err.response?.data?.error || 'Erro ao criar conta')
    } finally {
      setLoading(false)
    }
  }

  if (loadingInvitation) {
    return (
      <div className="w-full">
        <Card className="shadow-lg">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aviation-600"></div>
              <p className="text-slate-600">Carregando convite...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (success) {
    return (
      <div className="w-full">
        <Card className="shadow-lg">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 text-center">
                {t('auth.registrationComplete', 'Registro Completo!')}
              </h2>
              <p className="text-slate-600 text-center max-w-md">
                {t('auth.checkEmail', 'Por favor, verifique seu email para ativar sua conta.')}
              </p>
              <p className="text-sm text-slate-500">
                {t('auth.redirecting', 'Redirecionando para login...')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="w-full">
      <Card className="shadow-lg">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">
            {t('auth.createAccount', 'Criar Conta')}
          </CardTitle>
          <CardDescription className="text-center">
            {invitation
              ? `Você foi convidado para ${invitation.organization_name}`
              : t('auth.registerSubtitle', 'Preencha os dados abaixo para criar sua conta')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email', 'Email')}</Label>
              <Controller
                control={control}
                name="email"
                render={({ field }) => (
                  <Input
                    {...field}
                    id="email"
                    type="email"
                    disabled={!!invitation}
                    autoCapitalize="none"
                    autoComplete="email"
                    placeholder="your@email.com"
                    className={errors.email ? 'border-red-500' : ''}
                  />
                )}
              />
              {errors.email && (
                <p className="text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            {/* First Name & Last Name */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">{t('auth.firstName', 'Nome')}</Label>
                <Controller
                  control={control}
                  name="firstName"
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="firstName"
                      type="text"
                      placeholder="João"
                      className={errors.firstName ? 'border-red-500' : ''}
                    />
                  )}
                />
                {errors.firstName && (
                  <p className="text-sm text-red-600">{errors.firstName.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">{t('auth.lastName', 'Sobrenome')}</Label>
                <Controller
                  control={control}
                  name="lastName"
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="lastName"
                      type="text"
                      placeholder="Silva"
                      className={errors.lastName ? 'border-red-500' : ''}
                    />
                  )}
                />
                {errors.lastName && (
                  <p className="text-sm text-red-600">{errors.lastName.message}</p>
                )}
              </div>
            </div>

            {/* Role Selection - Only for non-invited users */}
            {!invitation && (
              <div className="space-y-2">
                <Label htmlFor="role">Tipo de Conta</Label>
                <Controller
                  control={control}
                  name="role"
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione seu tipo de conta" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="operator">Operador (Proprietário de aeronaves)</SelectItem>
                        <SelectItem value="pilot">Piloto</SelectItem>
                        <SelectItem value="maintenance">Mecânico</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.role && (
                  <p className="text-sm text-red-600">{errors.role.message}</p>
                )}

                {/* Warning for pilot/mechanic */}
                {(watchRole === 'pilot' || watchRole === 'maintenance') && (
                  <div className="flex items-start gap-2 p-3 bg-amber-50 border-l-4 border-amber-500 rounded">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-700">
                      Pilotos e mecânicos precisam de um convite de um operador para se registrar.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Organization Name - Only for operators */}
            {watchRole === 'operator' && !invitation && (
              <div className="space-y-2">
                <Label htmlFor="organizationName">Nome da Organização</Label>
                <Controller
                  control={control}
                  name="organizationName"
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="organizationName"
                      type="text"
                      placeholder="Ex: Aviação Brasil Ltda"
                      className={errors.organizationName ? 'border-red-500' : ''}
                    />
                  )}
                />
                {errors.organizationName && (
                  <p className="text-sm text-red-600">{errors.organizationName.message}</p>
                )}
              </div>
            )}

            {/* Show organization name for invited users */}
            {invitation && (
              <div className="space-y-2">
                <Label>Organização</Label>
                <Input
                  value={invitation.organization_name}
                  disabled
                  className="bg-slate-100"
                />
              </div>
            )}

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password', 'Senha')}</Label>
              <Controller
                control={control}
                name="password"
                render={({ field }) => (
                  <Input
                    {...field}
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    placeholder="••••••••"
                    className={errors.password ? 'border-red-500' : ''}
                  />
                )}
              />
              {errors.password && (
                <p className="text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t('auth.confirmPassword', 'Confirmar Senha')}</Label>
              <Controller
                control={control}
                name="confirmPassword"
                render={({ field }) => (
                  <Input
                    {...field}
                    id="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    placeholder="••••••••"
                    className={errors.confirmPassword ? 'border-red-500' : ''}
                  />
                )}
              />
              {errors.confirmPassword && (
                <p className="text-sm text-red-600">{errors.confirmPassword.message}</p>
              )}
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border-l-4 border-red-500 rounded">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Legal agreement */}
            <div className="bg-slate-50 border border-slate-200 rounded p-3">
              <p className="text-xs text-slate-600">
                Ao criar uma conta, você concorda com nossos{' '}
                <Link
                  to="/terms"
                  className="text-aviation-600 hover:text-aviation-700 underline"
                  target="_blank"
                >
                  Termos de Serviço
                </Link>{' '}
                e{' '}
                <Link
                  to="/privacy"
                  className="text-aviation-600 hover:text-aviation-700 underline"
                  target="_blank"
                >
                  Política de Privacidade
                </Link>.
              </p>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading || (watchRole !== 'operator' && !invitation)}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('auth.registering', 'Criando conta...')}
                </>
              ) : (
                t('auth.register', 'Criar Conta')
              )}
            </Button>

            <div className="text-center text-sm text-slate-600">
              {t('auth.alreadyHaveAccount', 'Já tem uma conta?')}{' '}
              <Link
                to="/auth/login"
                className="text-aviation-600 hover:text-aviation-700 font-semibold"
              >
                {t('auth.signIn', 'Entrar')}
              </Link>
            </div>

            <div className="text-center pt-4 border-t border-slate-200">
              <Link
                to="/about"
                className="text-xs text-slate-500 hover:text-slate-700"
              >
                {t('landing.hero.learnMore', 'Learn more about Flight Journal')}
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

export default RegisterPage
